import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Switch } from '../../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { 
  Shield,
  Database,
  Users,
  Building2,
  FileText,
  Zap,
  Save,
  Loader2,
  AlertCircle,
  Mail,
  Server,
  Eye,
  EyeOff,
  CheckCircle2,
  Edit2,
  Lock,
  ChevronDown,
  ChevronUp,
  Send
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsSystem = () => {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [smtpExists, setSmtpExists] = useState(false);
  const [smtpEditing, setSmtpEditing] = useState(false);
  const [smtpOpen, setSmtpOpen] = useState(true);
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalUsers: 0,
    totalProjects: 0
  });
  
  const [systemSettings, setSystemSettings] = useState({
    maintenanceMode: false,
    newRegistrations: true,
    requireEmailVerification: true,
    sessionTimeout: 24,
    maxUsersPerTenant: 50,
    maxProjectsPerTenant: 100
  });

  const [smtpSettings, setSmtpSettings] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    is_active: true
  });

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    if (isSuperAdmin) {
      fetchSystemStats();
      fetchSmtpSettings();
    }
  }, [isSuperAdmin]);

  const fetchSystemStats = async () => {
    try {
      const [tenantsRes, usersRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/tenants`, getAuthHeaders()),
        axios.get(`${API_URL}/users`, getAuthHeaders()),
        axios.get(`${API_URL}/projects`, getAuthHeaders())
      ]);
      setStats({
        totalTenants: tenantsRes.data.length,
        totalUsers: usersRes.data.length,
        totalProjects: projectsRes.data.length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchSmtpSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/smtp-settings/global`, getAuthHeaders());
      if (res.data) {
        setSmtpSettings({
          ...res.data,
          password: '' // Don't populate password field
        });
        setSmtpExists(true);
        setSmtpEditing(false);
        setSmtpOpen(false); // Collapse if settings exist
      }
    } catch (error) {
      // No settings yet
      setSmtpExists(false);
      setSmtpEditing(true);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      toast.success('System settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSmtp = async () => {
    if (!smtpSettings.host || !smtpSettings.username || !smtpSettings.from_email) {
      toast.error('Please fill in required SMTP fields');
      return;
    }

    setSmtpLoading(true);
    try {
      if (smtpExists) {
        // Update existing
        const updateData = { ...smtpSettings };
        if (!updateData.password) delete updateData.password; // Don't send empty password
        await axios.put(`${API_URL}/smtp-settings/global`, updateData, getAuthHeaders());
        toast.success('SMTP settings updated');
      } else {
        // Create new
        if (!smtpSettings.password) {
          toast.error('Password is required for new SMTP configuration');
          setSmtpLoading(false);
          return;
        }
        await axios.post(`${API_URL}/smtp-settings/global`, smtpSettings, getAuthHeaders());
        setSmtpExists(true);
        toast.success('SMTP settings created');
      }
      setSmtpSettings({ ...smtpSettings, password: '' });
      setSmtpEditing(false);
      setSmtpOpen(false); // Collapse after save
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SMTP settings');
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleTestSmtpEmail = async () => {
    setTestEmailLoading(true);
    try {
      await axios.post(`${API_URL}/admin/send-daily-digest`, {}, getAuthHeaders());
      toast.success('Test email sent! Check your inbox for the daily digest.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setTestEmailLoading(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Only super admin can access system settings</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in" data-testid="settings-system-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          System Settings
        </h1>
        <p className="text-slate-500 mt-1">Configure global system behavior and email settings</p>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Building2 size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalTenants}</p>
                <p className="text-xs text-slate-500">Tenants</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Users size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalUsers}</p>
                <p className="text-xs text-slate-500">Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                <FileText size={20} className="text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.totalProjects}</p>
                <p className="text-xs text-slate-500">Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                <Zap size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">Active</p>
                <p className="text-xs text-slate-500">Status</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* SMTP Configuration */}
      <Card className="border-slate-200 shadow-sm">
        <Collapsible open={smtpOpen} onOpenChange={setSmtpOpen}>
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail size={20} className="text-slate-500" />
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    Admin SMTP Configuration
                  </CardTitle>
                  <CardDescription>Configure email server for system notifications and admin emails</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {smtpExists && !smtpEditing && (
                  <span className="flex items-center gap-1 text-sm text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    <CheckCircle2 size={14} />
                    Configured
                  </span>
                )}
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm">
                    {smtpOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="p-6 space-y-4">
              {/* Locked State - Show when configured and not editing */}
              {smtpExists && !smtpEditing && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Lock size={18} className="text-slate-500" />
                    <div>
                      <p className="font-medium text-slate-900">SMTP Settings Locked</p>
                      <p className="text-sm text-slate-500">
                        Server: {smtpSettings.host}:{smtpSettings.port} | From: {smtpSettings.from_email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleTestSmtpEmail}
                      disabled={testEmailLoading}
                    >
                      {testEmailLoading ? (
                        <Loader2 size={14} className="mr-2 animate-spin" />
                      ) : (
                        <Send size={14} className="mr-2" />
                      )}
                      Test Email
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSmtpEditing(true)}
                    >
                      <Edit2 size={14} className="mr-2" />
                      Edit
                    </Button>
                  </div>
                </div>
              )}

              {/* Edit Form - Show when editing or no settings yet */}
              {(smtpEditing || !smtpExists) && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>SMTP Host *</Label>
                      <div className="relative">
                        <Server size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                          value={smtpSettings.host}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, host: e.target.value })}
                          placeholder="smtp.gmail.com"
                          className="pl-10"
                          data-testid="smtp-host-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Port *</Label>
                      <Input
                        type="number"
                        value={smtpSettings.port}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, port: parseInt(e.target.value) || 587 })}
                        placeholder="587"
                        data-testid="smtp-port-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Username *</Label>
                      <Input
                        value={smtpSettings.username}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, username: e.target.value })}
                        placeholder="your-email@gmail.com"
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password {smtpExists ? '(leave blank to keep current)' : '*'}</Label>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          value={smtpSettings.password}
                          onChange={(e) => setSmtpSettings({ ...smtpSettings, password: e.target.value })}
                          placeholder={smtpExists ? '••••••••' : 'Enter password'}
                          data-testid="smtp-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Email *</Label>
                      <Input
                        type="email"
                        value={smtpSettings.from_email}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, from_email: e.target.value })}
                        placeholder="noreply@yourdomain.com"
                        data-testid="smtp-from-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>From Name *</Label>
                      <Input
                        value={smtpSettings.from_name}
                        onChange={(e) => setSmtpSettings({ ...smtpSettings, from_name: e.target.value })}
                        placeholder="My Client PM"
                        data-testid="smtp-from-name-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-6 pt-2">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={smtpSettings.use_tls}
                        onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, use_tls: checked })}
                        data-testid="smtp-tls-switch"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-900">Use TLS</p>
                        <p className="text-xs text-slate-500">Enable TLS encryption</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={smtpSettings.is_active}
                        onCheckedChange={(checked) => setSmtpSettings({ ...smtpSettings, is_active: checked })}
                        data-testid="smtp-active-switch"
                      />
                      <div>
                        <p className="text-sm font-medium text-slate-900">Active</p>
                        <p className="text-xs text-slate-500">Enable email sending</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    {smtpExists && smtpEditing && (
                      <Button 
                        variant="outline"
                        onClick={() => {
                          setSmtpEditing(false);
                          fetchSmtpSettings();
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      onClick={handleSaveSmtp}
                      disabled={smtpLoading}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="save-smtp-btn"
                    >
                      {smtpLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save & Close
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* System Configuration */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Database size={20} className="text-slate-500" />
            System Configuration
          </CardTitle>
          <CardDescription>Configure global system behavior</CardDescription>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Maintenance Mode</p>
              <p className="text-sm text-slate-500">Temporarily disable access for non-admin users</p>
            </div>
            <Switch
              checked={systemSettings.maintenanceMode}
              onCheckedChange={(checked) => setSystemSettings({ ...systemSettings, maintenanceMode: checked })}
              data-testid="maintenance-mode-switch"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Allow New User Creation</p>
              <p className="text-sm text-slate-500">Allow admins to create new user accounts</p>
            </div>
            <Switch
              checked={systemSettings.newRegistrations}
              onCheckedChange={(checked) => setSystemSettings({ ...systemSettings, newRegistrations: checked })}
              data-testid="new-registrations-switch"
            />
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Require Email Verification</p>
              <p className="text-sm text-slate-500">Users must verify email on first login</p>
            </div>
            <Switch
              checked={systemSettings.requireEmailVerification}
              onCheckedChange={(checked) => setSystemSettings({ ...systemSettings, requireEmailVerification: checked })}
              data-testid="email-verification-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Limits */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Shield size={20} className="text-slate-500" />
            System Limits
          </CardTitle>
          <CardDescription>Set default limits for tenants</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Session Timeout (hours)</Label>
              <Input
                type="number"
                value={systemSettings.sessionTimeout}
                onChange={(e) => setSystemSettings({ ...systemSettings, sessionTimeout: parseInt(e.target.value) || 24 })}
                min={1}
                max={168}
                data-testid="session-timeout-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Users per Tenant</Label>
              <Input
                type="number"
                value={systemSettings.maxUsersPerTenant}
                onChange={(e) => setSystemSettings({ ...systemSettings, maxUsersPerTenant: parseInt(e.target.value) || 50 })}
                min={1}
                data-testid="max-users-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Max Projects per Tenant</Label>
              <Input
                type="number"
                value={systemSettings.maxProjectsPerTenant}
                onChange={(e) => setSystemSettings({ ...systemSettings, maxProjectsPerTenant: parseInt(e.target.value) || 100 })}
                min={1}
                data-testid="max-projects-input"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Warning */}
      <Card className="border-amber-200 bg-amber-50 shadow-sm">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-800">Super Admin Access</p>
            <p className="text-sm text-amber-700 mt-1">
              You have full system access. Changes here affect all users and tenants.
              Please review changes carefully before saving.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={loading}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="save-system-settings-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save System Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsSystem;
